import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Radio,
  RadioGroup,
  RADIO_VALUE_STATE_LABELS,
  RADIO_VALUE_STATES,
  type RadioValueState,
} from '@enamelai/design-system'
import {
  RADIO_PREVIEW_STATE_LABELS,
  RADIO_PREVIEW_STATES,
} from '../showcaseConstants'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './RadioShowcase.css'

const DEFAULT_OPTIONS = [
  { value: 'option-1', label: 'Option 1' },
  { value: 'option-2', label: 'Option 2' },
  { value: 'option-3', label: 'Option 3' },
]

const getValueStateProps = (
  valueState: RadioValueState,
): { checked?: boolean } => ({
  checked: valueState === 'checked',
})

const RadioShowcase: React.FC = () => {
  const [groupLabel, setGroupLabel] = useState('Select an option')
  const [optionLabel, setOptionLabel] = useState('Option label')
  const [selectedValue, setSelectedValue] = useState('option-1')
  const [disabled, setDisabled] = useState(false)
  const [orientation, setOrientation] = useState<'vertical' | 'horizontal'>(
    'vertical',
  )
  const [highlightedValueState, setHighlightedValueState] =
    useState<RadioValueState>('unchecked')
  const rowRefs = useRef<
    Partial<Record<RadioValueState, HTMLDivElement | null>>
  >({})

  const liveOptions = useMemo(
    () =>
      DEFAULT_OPTIONS.map((option, index) => ({
        ...option,
        label: index === 0 ? optionLabel : option.label,
      })),
    [optionLabel],
  )

  useEffect(() => {
    rowRefs.current[highlightedValueState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedValueState])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'radio-highlight-value',
            label: 'Highlighted value',
            control: (
              <select
                id="radio-highlight-value"
                className="ui-field-control"
                value={highlightedValueState}
                onChange={(event) =>
                  setHighlightedValueState(
                    event.target.value as RadioValueState,
                  )
                }
              >
                {RADIO_VALUE_STATES.map((option) => (
                  <option key={option} value={option}>
                    {RADIO_VALUE_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'radio-orientation',
            label: 'Orientation',
            control: (
              <select
                id="radio-orientation"
                className="ui-field-control"
                value={orientation}
                onChange={(event) =>
                  setOrientation(
                    event.target.value as 'vertical' | 'horizontal',
                  )
                }
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'radio-group-label',
            label: 'Group label',
            control: (
              <input
                id="radio-group-label"
                className="ui-field-control"
                type="text"
                value={groupLabel}
                onChange={(event) => setGroupLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'radio-option-label',
            label: 'First option label',
            control: (
              <input
                id="radio-option-label"
                className="ui-field-control"
                type="text"
                value={optionLabel}
                onChange={(event) => setOptionLabel(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'radio-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="radio-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, groupLabel, highlightedValueState, optionLabel, orientation],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Radio"
        description="Single-select control for mutually exclusive choices, with grouped and standalone usage."
        stageClassName="ui-preview__stage--wide radio-showcase__stage-wrap"
      >
        <div className={`radio-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Interactive radio group — choose one option. Only one selection is
              allowed at a time.
            </p>
            <RadioGroup
              label={groupLabel}
              value={selectedValue}
              options={liveOptions}
              disabled={disabled}
              orientation={orientation}
              onChange={setSelectedValue}
            />
            <p className="radio-showcase__selection-note">
              Selected: {selectedValue}
            </p>
          </section>

          <section className="radio-showcase__matrix">
            <h4 className="radio-showcase__section-title">
              Interaction states
            </h4>
            <div className="radio-showcase__matrix-header">
              <span className="radio-showcase__matrix-corner">Value</span>
              {RADIO_PREVIEW_STATES.map((state) => (
                <span
                  key={state}
                  className="radio-showcase__matrix-header-cell"
                >
                  {RADIO_PREVIEW_STATE_LABELS[state]}
                </span>
              ))}
            </div>

            {RADIO_VALUE_STATES.map((valueState) => (
              <div
                key={valueState}
                ref={(element) => {
                  rowRefs.current[valueState] = element
                }}
                className={`radio-showcase__matrix-row${
                  valueState === highlightedValueState
                    ? ' radio-showcase__matrix-row--selected'
                    : ''
                }`}
              >
                <span className="radio-showcase__matrix-row-label">
                  {RADIO_VALUE_STATE_LABELS[valueState]}
                </span>
                {RADIO_PREVIEW_STATES.map((previewState) => (
                  <div
                    key={`${valueState}-${previewState}`}
                    className="radio-showcase__matrix-cell"
                  >
                    <Radio
                      value={valueState}
                      {...getValueStateProps(valueState)}
                      previewState={previewState}
                      aria-label={`${RADIO_VALUE_STATE_LABELS[valueState]} ${RADIO_PREVIEW_STATE_LABELS[previewState]}`}
                    />
                  </div>
                ))}
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Radio" sections={propertySections} />
    </>
  )
}

export default RadioShowcase
